import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import ServicesView from 'components/organisms/catalogue/tabs/components/services/view';
import { 
  getVenueServices, 
  getVenueServiceOfferings, 
  updateVenueServices 
} from 'api';

const ServicesContainer = ({ venueData, onVenueUpdate }) => {
  // Current venue services (assigned to this venue)
  const [selectedServices, setSelectedServices] = useState([]);
  
  // Available services for search dropdown
  const [availableServices, setAvailableServices] = useState([]);
  
  // UI state for the ServicesView
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredServices, setFilteredServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });


  // Snackbar helper function
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Load current venue services
  const loadVenueServices = useCallback(async () => {
    if (!venueData?.slug) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getVenueServiceOfferings(venueData.slug);
      const services = response?.data || response || [];
      
      // Transform API response to match what ServicesView expects
      const formattedServices = services.map(service => ({
        slug: service.service_slug,
        name: service.service_name,
        description: service.description || service.service_description,
        priority: service.priority,
        is_featured: service.is_featured,
        // Keep original API data for updates
        service_slug: service.service_slug,
        service_name: service.service_name,
        service_description: service.service_description
      }));
      
      setSelectedServices(formattedServices);
    } catch (err) {
      console.error('Failed to load venue services:', err);
      setError('Failed to load venue services');
      showSnackbar('Failed to load venue services', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [venueData?.slug, showSnackbar]);

  // Load available services for search dropdown
  const loadAvailableServices = useCallback(async () => {
    setIsLoadingServices(true);
    try {
      const response = await getVenueServices();
      const services = response?.data?.results || response?.data || response || [];
      setAvailableServices(services);
    } catch (err) {
      console.error('Failed to load available services:', err);
      showSnackbar('Failed to load available services', 'error');
    } finally {
      setIsLoadingServices(false);
    }
  }, [showSnackbar]);

  // Initial load
  useEffect(() => {
    loadVenueServices();
    loadAvailableServices();
  }, [loadVenueServices, loadAvailableServices]);

  // Filter services based on search query
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase().trim();
      const availableForSearch = availableServices.filter(service =>
        !selectedServices.some(selected => selected.slug === service.slug)
      );
      
      const filtered = availableForSearch.filter(service => {
        const name = service.name.toLowerCase();
        const desc = (service.description || '').toLowerCase();
        return name.includes(query) || desc.includes(query);
      });
      
      setFilteredServices(filtered);
      setShowDropdown(true);
    } else {
      setFilteredServices([]);
      setShowDropdown(false);
    }
  }, [searchQuery, availableServices, selectedServices]);

  // Search handlers
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleServiceSelect = (service) => {
    const newPriority = selectedServices.length > 0 
      ? Math.max(...selectedServices.map(s => s.priority || 0)) + 1 
      : 1;
    
    const newService = {
      slug: service.slug,
      name: service.name,
      description: '', // Start with empty venue-specific description
      priority: newPriority,
      is_featured: false,
      // API fields
      service_slug: service.slug,
      service_name: service.name,
      service_description: service.description
    };
    
    setSelectedServices(prev => [...prev, newService]);
    setHasChanges(true);
    setSearchQuery('');
    setShowDropdown(false);
    showSnackbar(`${service.name} added to venue services`, 'success');
  };

  const handleClickAway = () => {
    setShowDropdown(false);
  };

  // Service action handlers
  const handleRemoveService = (serviceSlug) => {
    setSelectedServices(prev => prev.filter(s => s.slug !== serviceSlug));
    setHasChanges(true);
    
    const removedService = selectedServices.find(s => s.slug === serviceSlug);
    showSnackbar(`${removedService?.name || 'Service'} removed`, 'success');
  };

  const movePriorityUp = (serviceSlug) => {
    setSelectedServices(prev => {
      const services = [...prev].sort((a, b) => a.priority - b.priority);
      const index = services.findIndex(s => s.slug === serviceSlug);
      
      if (index > 0) {
        // Swap priorities
        const temp = services[index].priority;
        services[index].priority = services[index - 1].priority;
        services[index - 1].priority = temp;
        setHasChanges(true);
      }
      
      return services;
    });
  };

  const movePriorityDown = (serviceSlug) => {
    setSelectedServices(prev => {
      const services = [...prev].sort((a, b) => a.priority - b.priority);
      const index = services.findIndex(s => s.slug === serviceSlug);
      
      if (index < services.length - 1 && index >= 0) {
        // Swap priorities
        const temp = services[index].priority;
        services[index].priority = services[index + 1].priority;
        services[index + 1].priority = temp;
        setHasChanges(true);
      }
      
      return services;
    });
  };

  // Edit dialog handlers
  const startEditing = (service) => {
    setEditingService({ ...service });
    setEditDialogOpen(true);
  };

  const saveServiceChanges = async () => {
    if (editingService) {
      // Update local state immediately
      setSelectedServices(prev => 
        prev.map(service => 
          service.slug === editingService.slug ? editingService : service
        )
      );
      
      setEditingService(null);
      setEditDialogOpen(false);
      setHasChanges(true);
      showSnackbar("Service description updated", "success");
    }
  };

  const cancelEditing = () => {
    setEditingService(null);
    setEditDialogOpen(false);
  };

  const handleDescriptionChange = (e) => {
    if (editingService) {
      setEditingService({ ...editingService, description: e.target.value });
    }
  };

  // Save all changes to backend
  const handleSaveServices = async () => {
    if (!venueData?.slug || !hasChanges) return;
    
    setIsSaving(true);
    
    try {
      const formattedServices = selectedServices.map(service => ({
        service_slug: service.service_slug || service.slug,
        description: service.description || '',
        priority: service.priority || 1,
        is_featured: service.is_featured || false
      }));

      await updateVenueServices(venueData.slug, formattedServices);
      
      // Reload to get fresh data
      await loadVenueServices();
      setHasChanges(false);
      showSnackbar('Services updated successfully', 'success');

      // Notify parent to refresh venue data
      if (onVenueUpdate) {
        onVenueUpdate();
      }
    } catch (err) {
      console.error('Failed to save services:', err);
      showSnackbar('Failed to save services: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <ServicesView
        selectedServices={selectedServices}
        searchQuery={searchQuery}
        showDropdown={showDropdown}
        filteredServices={filteredServices}
        isSaving={isSaving}
        isLoadingServices={isLoadingServices}
        hasChanges={hasChanges}
        error={error}
        editingService={editingService}
        editDialogOpen={editDialogOpen}
        disableSaveButton={!hasChanges}
        handleSearchChange={handleSearchChange}
        handleServiceSelect={handleServiceSelect}
        handleRemoveService={handleRemoveService}
        handleSaveServices={handleSaveServices}
        movePriorityUp={movePriorityUp}
        movePriorityDown={movePriorityDown}
        startEditing={startEditing}
        saveServiceChanges={saveServiceChanges}
        cancelEditing={cancelEditing}
        handleDescriptionChange={handleDescriptionChange}
        handleClickAway={handleClickAway}
        
        // Additional props for compatibility
        venueData={venueData}
        isLoading={isLoading}
      />

      {/* Snackbar following the same pattern as your examples */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ServicesContainer;